/**
 * The PlayForward walk-in: nine beats from the Arcade door to MelzAI waking.
 *
 * Founder-defined sequence. One entry per plate — adding or reordering a beat is
 * an edit here and nothing else. Advance is CLICK-driven, never auto-played: a
 * judge on stage must be able to steer, and click-to-advance is the convention
 * this art is already speaking.
 *
 * `image` keys point at lib/tokens.js. Where the founder's final plate has not
 * landed yet, a near-neighbour stands in and is marked `placeholder: true`.
 */

export const BEATS = [
  {
    key: "door",
    image: "pfArcadeApproach",
    kicker: "Pursuital Arcade",
    line: "The doors are shut. They are lit from the inside.",
    gate: true, // email is captured ON the door; submit walks you through
    nav: { dir: "in", x: 50, y: 66, label: "Go in" },
  },
  {
    key: "enter",
    image: "pfArcadeEnter",
    kicker: "Twenty feet in",
    line: "You stop. Screens everywhere, and every one of them is about you.",
    lowres: true,
    nav: { dir: "left", x: 17, y: 52, label: "Look left" },
  },
  {
    key: "look_left",
    image: "pfLookLeft",
    kicker: "You turn left",
    line: "A leaderboard. Your name is on it, further down than you would like.",
    lowres: true,
    nav: { dir: "right", x: 84, y: 52, label: "Look right" },
  },
  {
    key: "look_right",
    image: "pfLookRight",
    kicker: "Back to centre, you turn right",
    line: "You versus you. Nobody else is on that board.",
    lowres: true,
    nav: { dir: "in", x: 50, y: 70, label: "Walk on" },
  },
  {
    key: "deeper",
    image: "pfDeeper",
    kicker: "Thirty feet deeper",
    line: "The room keeps going. Nobody stops you — that is the point of this place.",
    lowres: true,
    nav: { dir: "right", x: 85, y: 54, label: "Look right" },
  },
  {
    key: "cove_look",
    image: "pfCoveLook",
    kicker: "You turn right again",
    line: "An alcove off the floor. One chair. A screen that is not on yet.",
    nav: { dir: "in", x: 50, y: 64, label: "The Cove" },
  },
  {
    key: "seated",
    image: "pfCoveSeat",
    kicker: "The Cove",
    line: "You sit down. The screen is close enough to read.",
    seat: true, // hands off to the Cove — zoom, wake, MelzAI
    nav: { dir: "in", x: 50, y: 74, label: "Take the chair" },
  },
];

/**
 * Where the screen sits inside the seated plate, measured off the founder's
 * render. Used to zoom the plate so the glass fills the frame, and to place the
 * live MelzAI panel exactly inside the bezel.
 */
export const SCREEN_RECT = { left: 30.5, right: 69.5, top: 19.55, bottom: 53.45 };

export const beatIndex = (key) => Math.max(0, BEATS.findIndex((b) => b.key === key));
export const SEAT_INDEX = BEATS.findIndex((b) => b.seat);

/**
 * Stageward: Clarity Hall. Four beats from the steps to the Graffiti Wall.
 *
 * Founder-defined, and the register changes on purpose — PlayForward is
 * Renaissance neon, Clarity Hall is baroque. You are backstage at an opera
 * house now, and the work is heavier.
 *
 * Note the joke that is also the point: the door says GREEN ROOM. It is a coat
 * closet. Nobody is performing yet — you are still taking your coat off, and
 * what comes off with it goes on the wall.
 */
export const SW_BEATS = [
  {
    key: "hall",
    image: "swHallApproach",
    kicker: "Clarity Hall",
    line: "Different building. Different weather. The doors are open.",
    nav: { dir: "in", x: 50, y: 72, label: "Go in" },
  },
  {
    key: "foyer",
    image: "swFoyer",
    kicker: "The Foyer",
    line: "Marble, gilt, two staircases and nobody watching. The stage is upstairs. You are not going upstairs yet.",
    nav: { dir: "left", x: 13, y: 50, label: "Look left" },
  },
  {
    key: "greenroom_door",
    image: "swGreenRoomDoor",
    kicker: "A door on the left",
    line: "It says GREEN ROOM. It is a coat closet. That is honest — nobody is performing yet.",
    nav: { dir: "in", x: 32, y: 58, label: "The Green Room" },
  },
  {
    key: "graffiti",
    image: "swGraffitiWall",
    kicker: "The Green Room",
    line: "One bench, one blank wall, and no audience. Put down what you have been carrying.",
    arrive: true, // hands off to the Graffiti Wall
    nav: { dir: "in", x: 50, y: 46, label: "The wall" },
  },
];

export const SW_ARRIVE_INDEX = SW_BEATS.findIndex((b) => b.arrive);

/**
 * Where the blank panel sits inside the Graffiti Wall plate, measured off the
 * founder's render. Shards are scrawled inside this rectangle and nowhere else.
 */
export const WALL_RECT = { left: 12, right: 87, top: 25, bottom: 74 };

/**
 * Stageward, second leg: out of the coat closet and up to the Drawing Table.
 *
 * The Graffiti Wall was the dump. This is the turn: MelzAI carries the Shards
 * off the wall and lays them on vellum, where they get gathered into Stacks and
 * the Stacks get sequenced into Clusters.
 */
export const SW_DRAW_BEATS = [
  {
    key: "greenroom_exit",
    image: "swGreenRoomExit",
    kicker: "Still the Green Room",
    line: "Coat on the hook. The wall is bare again and MelzAI has the lot. There is a door.",
    nav: { dir: "in", x: 52, y: 60, label: "Through" },
  },
  {
    key: "drawing_room",
    image: "swDrawingRoom",
    kicker: "The Drawing Room",
    line: "Candlelight, a screen that is not on, and a table by the wall with nothing on it yet.",
    nav: { dir: "left", x: 15, y: 54, label: "The table" },
  },
  {
    key: "table_approach",
    image: "swTableApproach",
    kicker: "The Drawing Table",
    line: "Vellum, weighted flat. Big enough for everything you just put down, and empty on purpose.",
    nav: { dir: "in", x: 46, y: 52, label: "Sit" },
  },
  {
    key: "table",
    image: "swTableFront",
    kicker: "You sit",
    line: "MelzAI sets the Shards out where you can see all of them at once. Now they get gathered.",
    arrive: true,
    nav: { dir: "in", x: 50, y: 50, label: "Lay them out" },
  },
];

export const SW_DRAW_ARRIVE_INDEX = SW_DRAW_BEATS.findIndex((b) => b.arrive);

/** The blank vellum inside swDrawingSurface. Stacks are laid out here only. */
export const VELLUM_RECT = { left: 5, right: 95, top: 7, bottom: 92 };
